import { DataTable } from "primereact/datatable";
import { ReactNode } from "react";
import styled from "styled-components";

// The redesigned order view section header: grey rounded bar, purple icon
// square, bold title, and an optional right-aligned action (the pencil).
export function SectionHeader({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div
      className="flex flex-wrap align-items-center gap-3 p-2 border-round-3xl border-1"
      style={{
        background:
          "color-mix(in srgb, var(--brand-gray-100) 80%, transparent)",
        borderColor: "var(--brand-gray-200)",
        boxShadow:
          "0 1px 2px color-mix(in srgb, var(--brand-black) 4%, transparent), 0 6px 16px color-mix(in srgb, var(--brand-black) 8%, transparent)",
      }}
    >
      <div
        className="flex align-items-center justify-content-center border-round-2xl flex-shrink-0"
        style={{
          width: "2.5rem",
          height: "2.5rem",
          background: "var(--brand-purple-600)",
          color: "var(--brand-white)",
        }}
      >
        <i className={`${icon} text-base`} />
      </div>
      <span
        className="text-base font-bold"
        style={{ color: "var(--brand-black)" }}
      >
        {title}
      </span>
      <div
        className="ml-auto flex align-items-center"
        style={{ height: "2.5rem" }}
      >
        {children}
      </div>
    </div>
  );
}

// The redesigned order view table: grey rows, grey headers and gray-200 lines,
// shared by the procedure, adjustment, responsibility and payout tables.
export const SectionTable = styled(DataTable)`
  /* The && repeats the generated class so these outrank the theme's own
     .p-datatable rules, which are otherwise equally specific. */
  && .p-datatable-header {
    background: var(--brand-gray-50);
    color: var(--brand-black);
    border-width: 0 0 1px 0;
    border-color: var(--brand-gray-200);
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    font-weight: 700;
  }

  && .p-datatable-thead > tr > th {
    background: var(--brand-gray-50);
    color: var(--brand-gray-600);
    font-weight: 500;
    font-size: 0.75rem;
    border-color: var(--brand-gray-200);
    padding: 0.625rem 1rem;
  }

  && .p-datatable-tbody > tr {
    background: var(--brand-gray-50);
    color: color-mix(in srgb, var(--brand-black) 85%, transparent);
  }

  && .p-datatable-tbody > tr > td {
    color: color-mix(in srgb, var(--brand-black) 85%, transparent);
    font-size: 0.875rem;
    font-weight: 500;
    border-color: var(--brand-gray-200);
    padding: 0.75rem 1rem;
  }

  && .p-datatable-tbody > tr:last-child > td {
    border-bottom-width: 0;
  }

  && .p-datatable-tfoot > tr > td {
    background: var(--brand-gray-50);
    color: var(--brand-black);
    font-size: 0.875rem;
    font-weight: 700;
    border-color: var(--brand-gray-200);
    border-width: 1px 0 0 0;
    padding: 0.75rem 1rem;
  }
`;

// One read-only label/value pair of a section's two-column grid.
export function DetailField({
  label,
  value,
  pii,
  testId,
}: {
  label: string;
  value?: string | null;
  pii?: string;
  testId?: string;
}) {
  return (
    <div className="col-12 md:col-6 flex flex-column gap-1 mb-3">
      <span className="text-sm" style={{ color: "var(--brand-gray-600)" }}>
        {label}
      </span>
      <span
        className="text-base font-medium"
        style={{ color: "var(--brand-black)" }}
        data-pii={pii}
        data-testid={testId}
      >
        {value || "—"}
      </span>
    </div>
  );
}

// Read-only label and value for a form slot that shows an input while editing.
export function ReadOnlyLabel({ children }: { children: ReactNode }) {
  return (
    <span className="block text-sm" style={{ color: "var(--brand-gray-600)" }}>
      {children}
    </span>
  );
}

export function ReadOnlyValue({ children }: { children: ReactNode }) {
  return (
    <span
      className="block text-base font-medium"
      style={{ color: "var(--brand-black)" }}
    >
      {children}
    </span>
  );
}
